import React from 'react';
import { Table, Button } from 'react-bootstrap';
import { FaFileExcel } from 'react-icons/fa';

const formatarBRL = (valor) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    valor || 0
  );

const agruparItens = (itens) => {
  const grupos = {};

  itens.forEach((item) => {
    const chaveGe = item.codigo_grupo_empresa;
    if (!grupos[chaveGe]) {
      grupos[chaveGe] = { codigo_grupo_empresa: chaveGe, empresas: {}, count: 0, custo_total: 0 };
    }

    const chaveEmpresa = item.codigo_empresa;
    if (!grupos[chaveGe].empresas[chaveEmpresa]) {
      grupos[chaveGe].empresas[chaveEmpresa] = {
        codigo_empresa: chaveEmpresa,
        razao: item.razao,
        familias: {},
        count: 0,
        custo_total: 0,
      };
    }

    const chaveFamilia = item.codigo_familia;
    if (!grupos[chaveGe].empresas[chaveEmpresa].familias[chaveFamilia]) {
      grupos[chaveGe].empresas[chaveEmpresa].familias[chaveFamilia] = {
        codigo_familia: chaveFamilia,
        nome_familia: item.nome_familia,
        itens: [],
        count: 0,
        custo_total: 0,
      };
    }

    grupos[chaveGe].empresas[chaveEmpresa].familias[chaveFamilia].itens.push(item);
    grupos[chaveGe].empresas[chaveEmpresa].familias[chaveFamilia].count += 1;
    grupos[chaveGe].empresas[chaveEmpresa].familias[chaveFamilia].custo_total +=
      item.convertido || 0;

    grupos[chaveGe].empresas[chaveEmpresa].count += 1;
    grupos[chaveGe].empresas[chaveEmpresa].custo_total += item.convertido || 0;

    grupos[chaveGe].count += 1;
    grupos[chaveGe].custo_total += item.convertido || 0;
  });

  return grupos;
};

const TabelaResultado = ({ resultado, onExportarExcel, isExportando }) => {
  if (!resultado) return null;

  const { itens, total_geral, custo_total_geral } = resultado;

  if (!itens || itens.length === 0) {
    return (
      <div className="text-center text-muted py-5">
        <p>Nenhum registro encontrado para os filtros selecionados.</p>
      </div>
    );
  }

  const grupos = agruparItens(itens);

  const linhas = [];

  Object.values(grupos).forEach((grupo) => {
    linhas.push(
      <tr key={`ge-${grupo.codigo_grupo_empresa}`} style={{ backgroundColor: '#d0e4f7', fontWeight: 'bold' }}>
        <td colSpan={7}>GE: {grupo.codigo_grupo_empresa}</td>
        <td className="text-right">{grupo.count} imóvel(is)</td>
        <td className="text-right">{formatarBRL(grupo.custo_total)}</td>
      </tr>
    );

    Object.values(grupo.empresas).forEach((empresa) => {
      linhas.push(
        <tr
          key={`empresa-${grupo.codigo_grupo_empresa}-${empresa.codigo_empresa}`}
          style={{ backgroundColor: '#e8f4e8', fontWeight: 'bold' }}
        >
          <td colSpan={7} className="pl-4">
            &nbsp;&nbsp;Empresa: {empresa.razao}
          </td>
          <td className="text-right">{empresa.count} imóvel(is)</td>
          <td className="text-right">{formatarBRL(empresa.custo_total)}</td>
        </tr>
      );

      Object.values(empresa.familias).forEach((familia) => {
        linhas.push(
          <tr
            key={`familia-${grupo.codigo_grupo_empresa}-${empresa.codigo_empresa}-${familia.codigo_familia}`}
            style={{ backgroundColor: '#f5f5f5', fontStyle: 'italic' }}
          >
            <td colSpan={7} className="pl-5">
              &nbsp;&nbsp;&nbsp;&nbsp;Empreendimento: {familia.nome_familia}
            </td>
            <td className="text-right">{familia.count} imóvel(is)</td>
            <td className="text-right">{formatarBRL(familia.custo_total)}</td>
          </tr>
        );

        familia.itens.forEach((item) => {
          linhas.push(
            <tr
              key={`${item.codigo_grupo_empresa}-${item.codigo_empresa}-${item.codigo_familia}-${item.codigo_ativo}`}
            >
              <td>{item.codigo_ativo}</td>
              <td>{item.nome}</td>
              <td>{item.mapa1}</td>
              <td>{item.mapa2}</td>
              <td className="text-right">
                {item.area_total != null
                  ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.area_total)
                  : '-'}
              </td>
              <td>{item.status}</td>
              <td className="text-right">
                {item.custo_medio != null
                  ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.custo_medio)
                  : '-'}
              </td>
              <td />
              <td className="text-right">{formatarBRL(item.convertido)}</td>
            </tr>
          );
        });
      });
    });
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">
          Total geral: <strong>{total_geral}</strong> imóvel(is) —{' '}
          <strong>{formatarBRL(custo_total_geral)}</strong>
        </small>
        <Button
          variant="success"
          size="sm"
          onClick={onExportarExcel}
          disabled={isExportando}
        >
          <FaFileExcel className="mr-1" />
          {isExportando ? 'Exportando...' : 'Exportar Excel'}
        </Button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover size="sm">
          <thead>
            <tr>
              <th>Cód. Ativo</th>
              <th>Nome</th>
              <th>Mapa 1</th>
              <th>Mapa 2</th>
              <th className="text-right">Área Total</th>
              <th>Status</th>
              <th className="text-right">Custo Médio</th>
              <th className="text-right">Qtd</th>
              <th className="text-right">Custo BRL</th>
            </tr>
          </thead>
          <tbody>
            {linhas}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 'bold', backgroundColor: '#d0e4f7' }}>
              <td colSpan={7}>Total Geral</td>
              <td className="text-right">{total_geral} imóvel(is)</td>
              <td className="text-right">{formatarBRL(custo_total_geral)}</td>
            </tr>
          </tfoot>
        </Table>
      </div>
    </div>
  );
};

export default TabelaResultado;
